import axios from 'axios';
import ApiClient from '../ApiClient';
import SmtpConfigService from './SmtpConfigService';

const GMAIL_API = 'https://gmail.googleapis.com/gmail/v1/users/me';

const EMAIL_AUTH_STORAGE_KEY = "email_auth";
const EMAIL_AUTH_PROVIDERS_STORAGE_KEY = "email_auth_providers";
const EMAIL_AUTH_ACTIVE_KEY = "email_auth_active";

const toEmailAuthPayload = (emailType, accessToken = '', fromEmail = '') => ({
  emailType: emailType || '',
  fromEmail: fromEmail || '',
  accessToken: accessToken || '',
});

const getActiveEmailProvider = () => {
  try {
    return JSON.parse(localStorage.getItem(EMAIL_AUTH_ACTIVE_KEY)) || null;
  } catch {
    return null;
  }
};

const persistActiveEmailProvider = (emailType, meta = {}) => {
  localStorage.setItem(
    EMAIL_AUTH_ACTIVE_KEY,
    JSON.stringify({ emailType, ...meta }),
  );
};

const buildAuthFromRecord = (record) => {
  if (!record) return null;

  const emailType = record.emailtype || record.emailType || '';
  const accessToken = record.access_token || record.accessToken || '';
  const fromEmail = record.email || record.fromEmail || '';

  if (!emailType && !accessToken && !fromEmail) return null;

  return toEmailAuthPayload(emailType, accessToken, fromEmail);
};

const buildSmtpAuthPayload = () => {
  try {
    const active = getActiveEmailProvider();
    if (active?.emailType === 'smtp' && active.fromEmail) {
      return toEmailAuthPayload('smtp', active.accessToken || '', active.fromEmail);
    }

    const smtpSessions = JSON.parse(localStorage.getItem('smtp_sessions') || '[]');
    if (Array.isArray(smtpSessions) && smtpSessions.length > 0) {
      const configs = SmtpConfigService.getConfigs();
      const session = smtpSessions[0];
      const config = configs.find((item) => item.id === session.configId);
      if (config?.senderEmail) {
        return toEmailAuthPayload('smtp', '', config.senderEmail);
      }
    }

    const fallbackConfig = SmtpConfigService.getConfigs().find((item) => item.senderEmail);
    if (fallbackConfig) {
      return toEmailAuthPayload('smtp', '', fallbackConfig.senderEmail);
    }
  } catch {
    // ignore parse errors
  }

  return null;
};

const resolveStoredEmailAuthPayload = () => {
  const active = getActiveEmailProvider();

  if (active?.emailType === 'smtp') {
    const smtpAuth = buildSmtpAuthPayload();
    if (smtpAuth) return smtpAuth;
  }

  const providerTokens = getStoredProviderTokens();
  const emailAuthRecord = (() => {
    try {
      return JSON.parse(localStorage.getItem(EMAIL_AUTH_STORAGE_KEY));
    } catch {
      return null;
    }
  })();

  if (active?.emailType && active.emailType !== 'smtp') {
    const activeProvider = providerTokens[active.emailType];
    const activeFromProvider = buildAuthFromRecord(activeProvider);
    const activeFromEmailAuth = emailAuthRecord?.emailtype === active.emailType
      || emailAuthRecord?.emailType === active.emailType
      ? buildAuthFromRecord(emailAuthRecord)
      : null;

    if (activeFromEmailAuth?.accessToken) return activeFromEmailAuth;
    if (activeFromProvider?.accessToken) {
      return toEmailAuthPayload(
        active.emailType,
        activeFromProvider.accessToken,
        active.fromEmail || activeFromProvider.fromEmail || '',
      );
    }
  }

  const fromEmailAuth = buildAuthFromRecord(emailAuthRecord);
  if (fromEmailAuth?.accessToken) return fromEmailAuth;

  for (const emailType of ['gmail', 'zoho']) {
    const providerAuth = buildAuthFromRecord(providerTokens[emailType]);
    if (providerAuth?.accessToken) {
      if (emailType === 'gmail' && emailAuthRecord?.email && !providerAuth.fromEmail) {
        providerAuth.fromEmail = emailAuthRecord.email;
      }
      return providerAuth;
    }
  }

  return buildSmtpAuthPayload();
};

const gmailHeaders = (token) => ({ Authorization: `Bearer ${token}` });

const decodeBase64Url = (str) => {
  try {
    const base64 = str.replace(/-/g, '+').replace(/_/g, '/');
    const binary = atob(base64);
    const bytes = new Uint8Array(binary.length);
    for (let i = 0; i < binary.length; i++) bytes[i] = binary.charCodeAt(i);
    return new TextDecoder('utf-8').decode(bytes);
  } catch {
    return '';
  }
};

const extractBody = (payload) => {
  if (!payload) return { html: '', plain: '' };
  if (payload.body?.data) {
    const decoded = decodeBase64Url(payload.body.data);
    if (payload.mimeType === 'text/html') return { html: decoded, plain: '' };
    return { html: '', plain: decoded };
  }
  if (payload.parts?.length) {
    let html = '', plain = '';
    for (const part of payload.parts) {
      const r = extractBody(part);
      if (r.html) html = r.html;
      if (r.plain) plain = r.plain;
    }
    return { html, plain };
  }
  return { html: '', plain: '' };
};

const parseFrom = (fromStr = '') => {
  const match = fromStr.match(/^"?([^"<]+?)"?\s*<([^>]+)>$/);
  if (match) return { name: match[1].trim(), email: match[2].trim() };
  return { name: fromStr, email: fromStr };
};

const getStoredProviderTokens = () => {
    try {
        return JSON.parse(localStorage.getItem(EMAIL_AUTH_PROVIDERS_STORAGE_KEY)) || {};
    } catch {
        return {};
    }
};

const setStoredProviderTokens = (tokensByProvider) => {
    localStorage.setItem(
        EMAIL_AUTH_PROVIDERS_STORAGE_KEY,
        JSON.stringify(tokensByProvider)
    );
};

const EmailService = {
  async sendGmail(accessToken, to, subject, body) {
    if (!accessToken) throw new Error('Missing Gmail access token');
    const payload = { accessToken, to, subject, body };
    const resp = await ApiClient.post('/Email/send_gmail', payload);
    return resp;
  },

  async sendZoho(accessToken, to, subject, body, cc = [], bcc = [], isHtml = true, attachments = []) {
    if (!accessToken) throw new Error('Missing Zoho access token');
    
    const toArray = typeof to === 'string'
      ? to.split(',').map(email => email.trim()).filter(Boolean)
      : (Array.isArray(to) ? to : []);

    const ccArray = typeof cc === 'string'
      ? cc.split(',').map(email => email.trim()).filter(Boolean)
      : (Array.isArray(cc) ? cc : []);

    const bccArray = typeof bcc === 'string'
      ? bcc.split(',').map(email => email.trim()).filter(Boolean)
      : (Array.isArray(bcc) ? bcc : []);

    const payload = { 
      accessToken, 
      to: toArray, 
      cc: ccArray, 
      bcc: bccArray, 
      subject, 
      body, 
      isHtml, 
      attachments 
    };
    const resp = await ApiClient.post('/Email/send_zoho', payload);
    return resp;
  },

  async sendSmtp(toEmail, subject, body, attachments = []) {
    const payload = {
      ToEmail: toEmail,
      Subject: subject,
      Body: body,
      Attachments: attachments
    };
    const resp = await ApiClient.post('/Email/send_smtp', payload);
    return resp;
  },

  async getSmtpConfigurations() {
    return await ApiClient.get('/Email/smtp-configurations');
  },

  async getSmtpConfigurationById(id) {
    return await ApiClient.get(`/Email/smtp-configurations/${id}`);
  },

  async createSmtpConfiguration(config) {
    return await ApiClient.post('/Email/smtp-configurations', config);
  },

  async getGoogleUserInfo(accessToken) {
    if (!accessToken) throw new Error('Missing Google access token');
    const resp = await axios.get('https://www.googleapis.com/oauth2/v3/userinfo', {
      headers: gmailHeaders(accessToken),
    });
    return resp;
  },

  async getMessages(accessToken, labelId = 'INBOX', maxResults = 20) {
    if (!accessToken) throw new Error('Missing Gmail access token');
    const resp = await axios.get(`${GMAIL_API}/messages`, {
      headers: gmailHeaders(accessToken),
      params: { labelIds: labelId, maxResults },
    });
    return resp.data.messages || [];
  },

  async getDrafts(accessToken, maxResults = 20) {
    if (!accessToken) throw new Error('Missing Gmail access token');
    const resp = await axios.get(`${GMAIL_API}/drafts`, {
      headers: gmailHeaders(accessToken),
      params: { maxResults },
    });
    return resp.data.drafts || [];
  },

  async getMessageMeta(accessToken, id) {
    const url = new URL(`${GMAIL_API}/messages/${id}`);
    url.searchParams.append('format', 'metadata');
    ['Subject', 'From', 'Date', 'To'].forEach((h) => url.searchParams.append('metadataHeaders', h));
    const resp = await axios.get(url.toString(), {
      headers: gmailHeaders(accessToken),
    });
    const msg = resp.data;
    const headers = msg.payload?.headers || [];
    const h = (name) => headers.find((x) => x.name.toLowerCase() === name.toLowerCase())?.value || '';
    const from = parseFrom(h('From'));
    return {
      id: msg.id,
      threadId: msg.threadId,
      subject: h('Subject') || '(no subject)',
      from,
      date: h('Date'),
      snippet: msg.snippet || '',
      isUnread: (msg.labelIds || []).includes('UNREAD'),
      labelIds: msg.labelIds || [],
    };
  },

  async getMessage(accessToken, id) {
    const resp = await axios.get(`${GMAIL_API}/messages/${id}`, {
      headers: gmailHeaders(accessToken),
      params: { format: 'full' },
    });
    const msg = resp.data;
    const headers = msg.payload?.headers || [];
    const h = (name) => headers.find((x) => x.name.toLowerCase() === name.toLowerCase())?.value || '';
    const from = parseFrom(h('From'));
    const { html, plain } = extractBody(msg.payload);
    return {
      id: msg.id,
      threadId: msg.threadId,
      subject: h('Subject') || '(no subject)',
      from,
      to: h('To'),
      date: h('Date'),
      snippet: msg.snippet || '',
      html,
      plain,
      isUnread: (msg.labelIds || []).includes('UNREAD'),
      labelIds: msg.labelIds || [],
    };
  },

  getStoredGmailToken() {
    return this.getStoredProviderToken("gmail");
  },

  getStoredZohoToken() {
    return this.getStoredProviderToken("zoho");
  },

  getStoredGmailEmail() {
    try {
      const storedAuth = JSON.parse(localStorage.getItem(EMAIL_AUTH_STORAGE_KEY));
      if (storedAuth?.emailtype === 'gmail') return storedAuth.email || '';
      const providerEntry = getStoredProviderTokens().gmail;
      return providerEntry?.email || '';
    } catch {
      return '';
    }
  },

  getStoredProviderToken(emailtype) {
    const providerTokens = getStoredProviderTokens();
    const providerToken = providerTokens?.[emailtype]?.access_token;
    if (providerToken) return providerToken;

    try {
      const storedAuth = JSON.parse(localStorage.getItem(EMAIL_AUTH_STORAGE_KEY));
      return storedAuth?.emailtype === emailtype ? storedAuth.access_token : null;
    } catch {
      return null;
    }
  },

  setStoredEmailAuth(emailtype, accessToken, email = '') {
    const providerTokens = getStoredProviderTokens();
    providerTokens[emailtype] = { emailtype, access_token: accessToken, email };
    setStoredProviderTokens(providerTokens);

    localStorage.setItem(
      EMAIL_AUTH_STORAGE_KEY,
      JSON.stringify({ emailtype, access_token: accessToken, email }),
    );
    persistActiveEmailProvider(emailtype, { fromEmail: email, accessToken });
  },

  setActiveEmailProvider(emailType, meta = {}) {
    persistActiveEmailProvider(emailType, meta);
  },

  setActiveSmtpProvider(fromEmail, configId = '') {
    persistActiveEmailProvider('smtp', {
      fromEmail: fromEmail || '',
      configId,
      accessToken: '',
    });
  },

  getStoredEmailAuthPayload() {
    try {
      return resolveStoredEmailAuthPayload();
    } catch {
      return null;
    }
  },

  getCalendarEmailAuthPayload() {
    const resolved = resolveStoredEmailAuthPayload();
    if (!resolved) {
      return toEmailAuthPayload('', '', '');
    }

    const emailType = (resolved.emailType || '').toLowerCase();

    if (emailType === 'gmail') {
      return toEmailAuthPayload(
        'gmail',
        this.getStoredGmailToken() || resolved.accessToken || '',
        this.getStoredGmailEmail() || resolved.fromEmail || '',
      );
    }

    if (emailType === 'zoho') {
      return toEmailAuthPayload(
        'zoho',
        this.getStoredZohoToken() || resolved.accessToken || '',
        resolved.fromEmail || '',
      );
    }

    if (emailType === 'smtp') {
      return buildSmtpAuthPayload() || resolved;
    }

    return resolved;
  },

  async isGoogleAccessTokenValid(accessToken) {
    if (!accessToken) return false;

    try {
      const { data } = await axios.get('https://oauth2.googleapis.com/tokeninfo', {
        params: { access_token: accessToken },
      });

      if (data?.error) return false;

      const scope = String(data.scope || '');
      return (
        scope.includes('gmail.send')
        || scope.includes('https://www.googleapis.com/auth/gmail.send')
      );
    } catch {
      return false;
    }
  },

  async ensureCalendarEmailAuthPayload() {
    const payload = this.getCalendarEmailAuthPayload();

    if (payload.emailType !== 'gmail' || !payload.accessToken) {
      return payload;
    }

    const isValid = await this.isGoogleAccessTokenValid(payload.accessToken);
    if (!isValid) {
      throw new Error(
        'Your Gmail session has expired or is missing send permission. Please reconnect Gmail under Email settings.',
      );
    }

    if (!payload.fromEmail) {
      try {
        const userInfo = await this.getGoogleUserInfo(payload.accessToken);
        const email = userInfo.data?.email || '';
        if (email) {
          this.storeGmailAuth(payload.accessToken, email);
          payload.fromEmail = email;
        }
      } catch {
        // Keep going; backend may still accept the token.
      }
    }

    return payload;
  },

  storeGmailAuth(access_token, email = '') {
    this.setStoredEmailAuth('gmail', access_token, email);
  },

  storeZohoAuth(access_token, email = '') {
    this.setStoredEmailAuth('zoho', access_token, email);
  },

 clearStoredEmailAuth(emailtype) {
    try {
      if (!emailtype) {
        localStorage.removeItem(EMAIL_AUTH_STORAGE_KEY);
        localStorage.removeItem(EMAIL_AUTH_PROVIDERS_STORAGE_KEY);
        return;
      }

      const providerTokens = getStoredProviderTokens();
      delete providerTokens[emailtype];
      setStoredProviderTokens(providerTokens);

      const storedAuth = JSON.parse(localStorage.getItem(EMAIL_AUTH_STORAGE_KEY));
      if (storedAuth?.emailtype === emailtype) {
        localStorage.removeItem(EMAIL_AUTH_STORAGE_KEY);
      }

      const active = getActiveEmailProvider();
      if (active?.emailType === emailtype) {
        localStorage.removeItem(EMAIL_AUTH_ACTIVE_KEY);
      }
    } catch {}
  },

  isEmailConfigured() {
    if (this.getStoredGmailToken()) return true;
    if (this.getStoredZohoToken()) return true;

    try {
      const smtpSessions = JSON.parse(localStorage.getItem('smtp_sessions') || '[]');
      if (Array.isArray(smtpSessions) && smtpSessions.length > 0) return true;
    } catch {
      // ignore parse errors
    }

    return SmtpConfigService.getConfigs().some((config) => Boolean(config.senderEmail));
  },
};

export default EmailService;
